import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, type RefObject, type MutableRefObject } from 'react';
import { Box, Divider } from '@mui/material';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { I18N_KEYS } from '@/language/keys';
import { AddVariableButton } from './AddVariableButton';
import { MentionMenuItem } from './MentionMenuItem';
import type { AvailableInput } from './types';

interface MentionMenuContentProps {
  availableInputs: AvailableInput[];
  footerRef: RefObject<HTMLLIElement | null>;
  menuItemsRef: MutableRefObject<Map<string, HTMLLIElement>>;
  onInsertInputTag: (input: AvailableInput) => void;
  onAddVariableClick: () => void;
  queryText: string;
  onCreatePromptClick: () => void;
}

export const MentionMenuContent = ({
  availableInputs,
  footerRef,
  menuItemsRef,
  onInsertInputTag,
  onAddVariableClick,
  queryText,
  onCreatePromptClick,
}: MentionMenuContentProps) => {
  const { t } = useTranslation();

  // Callback to register menu item refs
  const setMenuItemRef = useCallback(
    (key: string, element: HTMLLIElement | null) => {
      if (element) {
        menuItemsRef.current.set(key, element);
      } else {
        menuItemsRef.current.delete(key);
      }
    },
    [menuItemsRef],
  );

  // Global keyboard handler for arrow key navigation
  // When the mention menu is open and focus is in the editor (not the menu),
  // ArrowDown/ArrowUp should move focus into the menu's first/last item.
  const handleGlobalArrowKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

      // Only handle when focus is NOT already inside the menu (menu handles its own navigation)
      const activeEl = document.activeElement;
      if (activeEl?.closest('[role="menu"]')) return;

      const scrollItems = Array.from(menuItemsRef.current.values());
      const allItems = [...scrollItems];
      if (footerRef.current) {
        allItems.push(footerRef.current);
      }

      if (allItems.length === 0) return;

      e.preventDefault(); // Prevent MUI's scroll-into-view behavior
      const target = e.key === 'ArrowDown' ? allItems[0] : allItems[allItems.length - 1];
      target.focus();
    },
    [footerRef, menuItemsRef],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleGlobalArrowKey);
    return () => document.removeEventListener('keydown', handleGlobalArrowKey);
  }, [handleGlobalArrowKey]);

  // When the user has typed text after "@", show only a "Create variable" button
  if (queryText.trim()) {
    return (
      <AppMenuItem ref={footerRef} onClick={onCreatePromptClick}>
        <AddVariableButton type="text" text={t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.CREATE_VARIABLE)} />
      </AppMenuItem>
    );
  }

  return (
    <>
      {availableInputs.length > 0 && (
        <Box
          sx={{
            maxHeight: '120px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {availableInputs.map((input) => (
            <MentionMenuItem
              key={input.key}
              input={input}
              onInsertInputTag={onInsertInputTag}
              setRef={setMenuItemRef}
            />
          ))}
        </Box>
      )}
      {availableInputs.length > 0 && <Divider />}
      <AppMenuItem ref={footerRef} onClick={onAddVariableClick}>
        <AddVariableButton type="text" />
      </AppMenuItem>
    </>
  );
};
